import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import LangSwitcher from "@/components/LangSwitcher";
import { LogOut, User2, Phone, ChevronDown } from "lucide-react";
import { DESTINATIONS_NAV } from "@/lib/destinations";
import http from "@/lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  // Pull live destinations from the API so the menu auto-adjusts when admin
  // renames slugs (e.g. "sakiz" → "sakizgezirehberi"). Falls back to the
  // static list if the request fails.
  const [destinations, setDestinations] = useState(DESTINATIONS_NAV);
  useEffect(() => {
    http.get("/destinations")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setDestinations(r.data.map((d) => ({
            slug: d.slug,
            name: (d.name || "").replace(/\s+(adası|adasi|island)$/i, "").trim() || d.name,
          })));
        }
      })
      .catch(() => { /* keep static fallback */ });
  }, []);

  return (
    <header className="sticky top-0 left-0 right-0 z-40 bg-[hsl(var(--bone))] border-b border-[hsl(var(--border))] shadow-sm" data-testid="site-header">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-3 flex items-center justify-between text-[hsl(var(--ink))]">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link" aria-label="Adalardatatil — Anasayfa">
          <img
            src="/brand/logo.png"
            alt="adalardatatil"
            className="h-24 md:h-24 w-auto max-w-[180px] md:max-w-none object-contain select-none drop-shadow-sm"
            draggable="false"
            loading="eager"
            decoding="async"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8 label-tiny">
          <Link to="/feribot" className="hover:text-[hsl(var(--terracotta))] transition" data-testid="nav-ferry">{t("nav.ferry")}</Link>
          <Link to="/otel" className="hover:text-[hsl(var(--terracotta))] transition" data-testid="nav-hotel">{t("nav.hotel")}</Link>
          <Link to="/tur" className="hover:text-[hsl(var(--terracotta))] transition" data-testid="nav-tour">{t("nav.tour")}</Link>
          <Link to="/arackiralama" className="hover:text-[hsl(var(--terracotta))] transition" data-testid="nav-car">{t("nav.car")}</Link>
          <Link to="/vize" className="hover:text-[hsl(var(--terracotta))] transition" data-testid="nav-visa">{t("nav.visa")}</Link>

          {/* Destinasyonlar dropdown */}
          <div className="relative group" data-testid="nav-destinations">
            <button className="inline-flex items-center gap-1 hover:text-[hsl(var(--terracotta))] transition focus:outline-none">
              Destinasyonlar <ChevronDown className="w-3 h-3" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
              <div className="bg-white border border-[hsl(var(--border))] shadow-lg min-w-[200px] py-2" data-testid="nav-destinations-menu">
                {destinations.map((d) => (
                  <Link
                    key={d.slug}
                    to={`/destinasyon/${d.slug}`}
                    className="block px-4 py-2 text-[hsl(var(--ink))] hover:bg-[hsl(var(--surface))] hover:text-[hsl(var(--terracotta))] transition normal-case tracking-normal font-normal"
                    data-testid={`nav-dest-${d.slug}`}
                  >
                    {d.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link to="/kampanyalar" className="hover:text-[hsl(var(--terracotta))] transition" data-testid="nav-campaigns">{t("nav.campaigns")}</Link>
          <Link to="/rezervasyon-sorgula" className="hover:text-[hsl(var(--terracotta))] transition" data-testid="nav-lookup">Rezervasyon Sorgula</Link>
        </nav>

        <div className="flex items-center gap-4">
          <a href="tel:+905453249892" className="hidden lg:flex items-center gap-2 label-tiny text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]" data-testid="phone-link">
            <Phone className="w-3.5 h-3.5" /> 0545 324 98 92
          </a>
          <LangSwitcher />
          {user ? (
            <div className="flex items-center gap-3">
              <Link to="/profil" className="label-tiny flex items-center gap-2 hover:text-[hsl(var(--terracotta))]" data-testid="profile-link">
                <User2 className="w-4 h-4" />{user.name?.split(" ")[0]}
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" className="label-tiny hover:text-[hsl(var(--terracotta))]" data-testid="admin-link">{t("auth.admin")}</Link>
              )}
              <button onClick={async () => { await logout(); nav("/"); }} className="label-tiny text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]" data-testid="logout-btn">
                <LogOut className="w-4 h-4 inline" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 label-tiny">
              <Link to="/giris" data-testid="login-link" className="hover:text-[hsl(var(--terracotta))]">{t("auth.login")}</Link>
              <Link to="/kayit" data-testid="register-link" className="px-3 py-1.5 bg-[hsl(var(--ink))] text-white hover:bg-[hsl(var(--terracotta))] transition">{t("auth.register")}</Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
